import { useHead } from "@unhead/react";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Box } from "@/components/layout/flexbox";
import { PublicFooter } from "@/components/nav/PublicFooter";
import { PublicNav } from "@/components/nav/PublicNav";
import { Container } from "@/components/pieces/bootstrap";
import { DOMAIN, SITE_NAME } from "@/css/constants";

export function PrivacyPolicyPage() {
  useHead({
    bodyAttrs: { class: "public-site" },
  });

  return (
    <>
      <SEOHead
        title={`Privacy Policy at ${SITE_NAME}`}
        description={`Learn more about ${SITE_NAME}’s privacy policy and practices, including what types of info ${SITE_NAME} receives and how info is used and shared.`}
        canonicalPath={`https://www.${DOMAIN}/privacy-policy`}
      />
      <PublicNav />
      <div className="public-content">
        <Container className="!max-w-marketing-content">
          <Box
            width="100%"
            maxWidth="750px"
            margin="100px auto 60px"
            lineHeight={1.6}
          >
            <Box mb={4}>
              <h3>{SITE_NAME} Privacy Policy</h3>
              <p>Last changed: July 24, 2024</p>
            </Box>
            <p>
              This Privacy Policy applies to your use of Exec.com, and any other
              websites, applications, or services provided, owned or operated by
              Exec Holdings, Inc. (with its affiliates, “Company”) that link to
              this Privacy Policy and include the website, talent development
              platform, and services (collectively, the “Services”). Company
              values the privacy of its users that visit and use the Services
              (collectively or individually, “you” or “users”) and wants you to
              be familiar with how we collect, use, and disclose personal
              information from and about you. This notice describes our Privacy
              Policy. By visiting Company or using the Services, you are
              accepting the practices described in this Privacy Policy, to the
              extent permitted by law.
            </p>
            <p>
              For purposes of data protection laws, Company, located at 440 N.
              Barranca #1890 Covina, CA 91723, is the data controller of your
              information collected in connection with the Services.
            </p>
            <p>
              You may share personal or financial information when using the
              Services, including submitting your personal contact and employer
              information, and other information necessary to obtain the
              Services.
            </p>
            <h3>Personal Information Company Collects About You</h3>
            <p>
              We obtain certain information about you when we provide the
              Services. Please note that we need certain types of information so
              that we can provide the Services to you. If you do not provide us
              with such information, or ask us to delete it, you may no longer
              be able to access or use the Services.
            </p>
            <p>
              <u>Information You Provide To Us.</u> You may provide information
              to us when using the Services, including personal contact
              information, employment details, and other information.
            </p>
            <p>
              <u>Automatically Collected Information.</u> When you access the
              Services or open one of our emails, we automatically record and
              store certain information about your device by using cookies
              (small text files sent to your browser), and other types of
              technology. Examples of information that we might collect are
              Internet Protocol addresses, device and browser types and
              identifiers, referring and exit page addresses, hashed identifiers
              derived from email addresses for the purposes of cross-device
              tracking for targeted advertising, and software and system types.
            </p>
            <p>
              <u>Information From Other Sources.</u> By signing up for the
              Services, we may obtain information about you from other sources
              when we have your permission.
            </p>
            <h3>Our Role as Data Controller and Processor</h3>
            <h4>Data Controller</h4>
            <p>
              We act as a data controller for personal information collected
              from:
            </p>
            <ul className="list-disc pl-6">
              <li>Website visitors</li>
              <li>Customers (organizations)</li>
              <li>Experts/coaches</li>
            </ul>
            <p>
              As a controller, we determine the purposes and means of processing
              this data.
            </p>
            <h4>Data Processor</h4>
            <p>
              For participant data, we typically act as a data processor on
              behalf of our customers (organizations). In this case:
            </p>
            <ul className="list-disc pl-6">
              <li>The customer is the data controller</li>
              <li>
                The customer determines how participant data should be processed
              </li>
            </ul>
            <p>
              If you are a participant with questions about your data, please
              contact your organization's administrator.
            </p>
            <h3>Information We Collect and Process for Different User Types</h3>
            <p>
              The following information provides an overview of the main types
              of data we collect and process for different user categories. This
              list is intended to be illustrative rather than exhaustive. The
              specific data we collect may vary depending on the particular
              services used, your interaction with our platform, and the choices
              you make. For a more comprehensive understanding of our data
              practices or for information about data not listed here, please
              contact us at privacy@exec.com.
            </p>
            <h4>Website Visitors</h4>
            <p>
              <b>Data Collected</b>:
            </p>
            <ul className="list-disc pl-6">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent on each page</li>
            </ul>
            <p>
              <b>How We Use This Data</b>:
            </p>
            <ul className="list-disc pl-6">
              <li>To improve website performance and user experience</li>
              <li>To analyze traffic patterns and user behavior</li>
            </ul>
            <h4>Customers (Organizations)</h4>
            <p>
              <b>Data Collected</b>:
            </p>
            <ul className="list-disc pl-6">
              <li>Company name and address</li>
              <li>Contact person's name, email, and phone number</li>
              <li>Billing information</li>
            </ul>
            <p>
              <b>How We Use This Data</b>:
            </p>
            <ul className="list-disc pl-6">
              <li>To provide and manage our Services</li>
              <li>For billing and account management</li>
              <li>To communicate about service updates and offers</li>
            </ul>
            <h4>Participants</h4>
            <p>
              <b>Data Collected</b>:
            </p>
            <ul className="list-disc pl-6">
              <li>Name and email address</li>
              <li>Job title and department</li>
              <li>Performance metrics and development goals</li>
            </ul>
            <p>
              <b>How We Use This Data</b>:
            </p>
            <ul className="list-disc pl-6">
              <li>To deliver personalized development programs</li>
              <li>To track progress and provide reports to the organization</li>
            </ul>
            <h4>Experts/Coaches</h4>
            <p>
              <b>Data Collected</b>:
            </p>
            <ul className="list-disc pl-6">
              <li>Professional qualifications and experience</li>
              <li>Availability and scheduling information</li>
              <li>Performance ratings and feedback</li>
            </ul>
            <p>
              <b>How We Use This Data</b>:
            </p>
            <ul className="list-disc pl-6">
              <li>To match experts with appropriate participants</li>
              <li>For quality assurance and performance evaluation</li>
            </ul>
            <h3>How Company Uses Personal Information</h3>
            <p>
              Company may use personal contact information to communicate with
              you to provide the Services and process payments.
            </p>
            <p>
              Company may also send you promotional materials or notifications
              related to the Services and place cookies on your browser for
              targeted advertising purposes.
            </p>
            <h3>Cookies and Similar Technologies</h3>
            <p>
              We use cookies and similar technologies to enhance your experience
              and analyze Service usage.
            </p>
            <h4>What Are Cookies</h4>
            <p>
              Small text files stored on your browser or device to remember
              preferences and settings.
            </p>
            <h4>Types of Cookies We Use</h4>
            <ol className="list-decimal pl-6">
              <li>
                <b>Essential Cookies</b>
                <ul className="list-disc pl-6">
                  <li>Purpose: Necessary for website functionality</li>
                  <li>Can't be disabled</li>
                </ul>
              </li>

              <li>
                <b>Preference Cookies</b>
                <ul className="list-disc pl-6">
                  <li>Purpose: Remember your settings and preferences</li>
                  <li>Can be disabled, may affect user experience</li>
                </ul>
              </li>

              <li>
                <b>Statistics Cookies</b>
                <ul className="list-disc pl-6">
                  <li>
                    Purpose: Analyze how visitors interact with our website
                  </li>
                  <li>Can be disabled</li>
                </ul>
              </li>

              <li>
                <b>Marketing Cookies</b>
                <ul className="list-disc pl-6">
                  <li>Purpose: Track visitors for targeted advertising</li>
                  <li>Can be disabled</li>
                </ul>
              </li>
            </ol>
            <h4>Your Cookie Choices</h4>
            You control through browser settings. Please note that limiting
            cookies may impact your experience.
            <h4>Other Technologies</h4>
            <ul className="list-disc pl-6">
              <li>Web beacons</li>
              <li>Pixels</li>
              <li>Similar tracking technologies</li>
            </ul>
            <h3>Data Retention</h3>
            <p>
              We retain your personal data for as long as necessary to provide
              our services and for legitimate and essential business purposes,
              such as:
            </p>
            <ul className="list-disc pl-6">
              <li>Maintaining the performance of our services</li>
              <li>
                Making data-driven business decisions about new features and
                offerings
              </li>
              <li>Complying with our legal obligations</li>
              <li>Resolving disputes</li>
            </ul>
            <p>
              When we no longer need personal data, we securely delete or
              destroy it.
            </p>
            <p>
              Retention periods can vary significantly based on the type of
              information and how it is used. Our retention periods are based on
              criteria that include legally mandated retention periods, pending
              or potential litigation, intellectual property or ownership
              rights, contract requirements, operational directives or needs,
              and historical archiving.
            </p>
            <h3>Data Security Measures</h3>
            <p>
              At Exec, we implement stringent security measures to protect your
              data:
            </p>
            <ul className="list-disc pl-6">
              <li>
                Encryption: We use industry-standard encryption for data in
                transit and at rest.
              </li>
              <li>
                Access Controls: We enforce strict access controls and
                authentication measures.
              </li>
              <li>
                Regular Audits: We conduct regular security audits and
                vulnerability assessments.
              </li>
              <li>
                Incident Response: We have a comprehensive incident response
                plan in place.
              </li>
              <li>
                Employee Training: Our staff undergoes regular security
                awareness training.
              </li>
            </ul>
            <p>
              For more details, please refer to our Security Whitepaper,
              available upon request.
            </p>
            <h3>Third-Party Data Sharing</h3>
            <p>
              We may share your data with third parties in the following
              circumstances:
            </p>
            <ul className="list-disc pl-6">
              <li>
                Service Providers: We engage trusted third parties to perform
                services on our behalf. These providers have limited access to
                your information to perform these tasks and are contractually
                obligated to use it only as directed by us.
              </li>
              <li>
                Business Transfers: If we're involved in a merger, acquisition,
                or sale of assets, your data may be transferred as part of that
                transaction.
              </li>
              <li>
                Legal Requirements: We may disclose your information when
                required by law, subpoena, or other legal process.
              </li>
            </ul>
            <h3>Subprocessors</h3>
            <p>
              Exec uses certain subprocessors to assist in providing our
              Services. A subprocessor is a third-party data processor engaged
              by Exec who has or potentially will have access to or process
              Service Data (which may contain Personal Data). Exec engages
              different types of subprocessors to perform various functions as
              explained below.
            </p>
            <p>
              Due to confidentiality restrictions, we cannot disclose the names
              of subprocessors we use. However, upon entering into a
              non-disclosure agreement, we can provide our enterprise customers
              with a detailed list of our subprocessors, including:
            </p>
            <ul className="list-disc pl-6">
              <li>Their name and location</li>
              <li>The activities they perform</li>
              <li>The types of data they process</li>
            </ul>
            <p>
              We maintain a robust vendor management program to ensure our
              subprocessors maintain appropriate privacy and security measures.
            </p>
            <p>
              If you'd like more information about our subprocessors, please
              contact us at privacy@exec.com.
            </p>
            <p>We do not sell your personal information to third parties.</p>
            <p>
              For a list of our key third-party service providers and their
              privacy policies, please contact us.
            </p>
            <h3>Use of Artificial Intelligence and Machine Learning</h3>
            <p>
              Company may use artificial intelligence (AI) and machine learning
              (ML) technologies to improve our Services and user experience.
              Here's how we approach the use of AI/ML with respect to your data:
            </p>
            <ul className="list-disc pl-6">
              <li>
                Purpose: We may use AI/ML for purposes such as personalizing
                user experiences, improving our Services, and detecting security
                threats.
              </li>

              <li>
                Data Used: The AI/ML systems may process various types of data,
                including usage data and content data. We design our AI systems
                to use the minimum amount of personal data necessary to achieve
                the intended purpose.
              </li>

              <li>
                Data Protection: All data used in our AI/ML systems is subject
                to the same rigorous security and privacy controls outlined in
                this policy.
              </li>

              <li>
                Data Segregation and Confidentiality: We maintain strict data
                segregation practices. Your personal identifiable information
                (PII), privileged business information, and intellectual
                property are never used to train models that could be accessed
                by or benefit other customers. Any AI/ML enhancements derived
                from your data are used exclusively to improve services for you
                and your employees.
              </li>

              <li>
                Automated Decision-Making: In some cases, our AI systems may
                make automated decisions that affect you. You have the right to
                request human intervention, express your point of view, and
                contest any decision made solely by automated means.
              </li>

              <li>
                Transparency: We strive to be transparent about where and how we
                use AI in our Services. If you have questions about how AI might
                be used in relation to your data, please contact us.
              </li>
            </ul>
            <p>
              We regularly review and update our AI/ML practices to ensure they
              align with evolving ethical standards and regulatory requirements.
              Our commitment to data confidentiality and segregation is
              paramount in all our AI/ML initiatives.
            </p>
            <h3>International Data Transfers</h3>
            <p>
              As a global company, we may transfer your data across
              international borders:
            </p>
            <ul className="list-disc pl-6">
              <li>
                Data Transfer Mechanisms: We use EU-approved Standard
                Contractual Clauses for data transfers out of the EEA.
              </li>
              <li>
                Storage Locations: Your data may be stored and processed in the
                United States or any other country where we or our service
                providers maintain facilities.
              </li>
              <li>
                Safeguards: We implement appropriate safeguards to ensure your
                data receives an adequate level of protection in the countries
                where we process it.
              </li>
            </ul>
            <h3>Legal Bases For Company Collecting Personal Information</h3>
            <p>
              Company collects and processes your Personal Information on the
              basis of different legal grounds, depending on the nature of the
              data provided, including:
            </p>
            <ul className="list-disc pl-6">
              <li>
                Performance of a Contract – In order to provide the Services,
                collection and processing Personal Information is necessary for
                the performance of our agreement with you.
              </li>
              <li>
                Legitimate Interest – We have other needs to collect or process
                Personal Information where we have a legitimate interest in
                providing marketing materials, where you have a reasonable
                expectation to perform a process or service on your behalf, or
                to prevent fraud and identity theft.
              </li>
              <li>
                Legal Obligation – Where we need to retain or provide
                information based on legal obligations, court orders, tax
                authority requests, and other obligations.
              </li>
              <li>
                Consent – Company relies upon your consent to collect and
                process Personal Information, especially where you rely upon
                Company to provide the Services.
              </li>
            </ul>
            <h3>Your Data Rights</h3>
            <p>
              Depending on your location, you may have the following rights
              regarding your personal data:
            </p>
            <ul className="list-disc pl-6">
              <li>Access: Request a copy of your personal data.</li>
              <li>Rectification: Correct inaccurate or incomplete data.</li>
              <li>
                Erasure: Request deletion of your data under certain
                circumstances.
              </li>
              <li>Restriction: Limit the processing of your data.</li>
              <li>
                Data Portability: Receive your data in a structured, commonly
                used format.
              </li>
              <li>
                Objection: Object to the processing of your data for certain
                purposes.
              </li>
              <li>
                Withdraw Consent: Revoke previously given consent for data
                processing.
              </li>
            </ul>
            <p>
              To exercise these rights, please contact us at privacy@exec.com.
              We will respond to your request within the timeframe required by
              applicable law.
            </p>
            <p>
              Note: Specific rights may vary based on your jurisdiction and the
              legal basis for processing your data.
            </p>
            <h3>Children's Privacy</h3>
            <p>
              Protecting the privacy of children is especially important.
              Company does not knowingly collect or maintain personal
              information of minors (as defined by the U.S. Children's Online
              Privacy Protection Act). If Company learns that personal
              information of minors has been collected on or through the
              Services, it will take reasonable measures to delete that
              information from its records. Company may not be used by children
              under the age of 13.
            </p>
            <h3>California Privacy Rights</h3>
            <p>
              California residents have specific rights under the California
              Consumer Privacy Act (CCPA) and the California Privacy Rights Act
              (CPRA):
            </p>
            <h4>Your Rights</h4>
            <ol className="list-decimal pl-6">
              <li>
                <b>Right to Know</b>: Request details about personal information
                we collect, use, and disclose
              </li>
              <li>
                <b>Right to Delete</b>: Request deletion of your personal
                information
              </li>
              <li>
                <b>Right to Correct</b>: Request correction of inaccurate
                personal information
              </li>
              <li>
                <b>Right to Opt-Out</b>: Opt-out of the sale or sharing of your
                personal information
              </li>
              <li>
                <b>Right to Limit</b>: Limit use and disclosure of sensitive
                personal information
              </li>
            </ol>
            <p>
              Contact us at privacy@exec.com and we will respond within the
              timeframe required by applicable law
            </p>
            <h4>Our Data Practices</h4>
            <ul className="list-disc pl-6">
              <li>
                We do not sell personal information as defined by the CCPA/CPRA
              </li>
              <li>
                No personal information sold to third parties in the preceding
                12 months
              </li>
              <li>
                We do not discriminate against users who exercise their
                CCPA/CPRA rights
              </li>
            </ul>
            <p>
              For detailed information on data collection and use, please refer
              to earlier sections of this policy.
            </p>
            <h3>Changes and Updates to This Privacy Policy</h3>
            <p>
              Company reserves the right to change, modify, add or delete
              portions of this Privacy Policy at any time. Your use of the
              Services constitutes your binding acceptance of such changes.
            </p>
            <h3>Company Contact Information</h3>
            <p>
              Please contact Company with any questions or comments about this
              Privacy Policy at hello@
              {DOMAIN}. If you are a California resident, you may have this same
              information emailed to you by sending a letter to the foregoing
              address with your email address and a request for information.
            </p>
          </Box>
        </Container>
      </div>
      <PublicFooter />
    </>
  );
}
